import React, { useEffect, useMemo, useRef, useState } from "react";
import SC from "../FileSearchInterface/style";
import { useSelector } from "react-redux";
import { apiGet, apiPost } from "api/callApi";
import TextFilterDropdown from "components/AdvancedSearchComponent/TextFilterDropdown";
import MultiSelectDropdown from "components/AdvancedSearchComponent/MultiSelectDropdown";
import CreatedOnDropdown from "components/AdvancedSearchComponent/CreatedOnDropdown";
import MoreFilterDropdown from "components/AdvancedSearchComponent/MoreFilterDropdown";
import SizeFilterDropdown from "components/AdvancedSearchComponent/SizeFilterDropdown";
import {
  jobTypes,
  projectType,
  queryType,
  queryTypes,
} from "utils/lookUpTable";
import RevisionFilterDropdown from "components/AdvancedSearchComponent/RevisionFilterDropdown";
import SaveFilterModal from "components/AdvancedSearchComponent/SaveFilterModal";
import SavedFilterDropdown from "components/AdvancedSearchComponent/SavedFilterDropdown";
import { fileTypeOptions, requestTypeForUpdate } from "utils/optionData/utils";
import { Button } from "@InnoUI/";
import theme from "@theme/";
import EmailFilterDropdown from "components/AdvancedSearchComponent/EmailFilterDropdown";
import AlarmDateDropdown from "components/AdvancedSearchComponent/AlarmDateDropdown";
import ActiveJournals from "./ActiveJournals";
import { useSearchParams } from "react-router-dom";
import InnoIcons from "@InnoIcons/";
import InteractiveSearchUI from "../InteractiveSearchUI";
import { AIServices } from "utils/helper";

const JournalSearchInterface = (props) => {
  const { setTotalRecord = () => {}, activeTab, filterSessionKey } = props;
  const [hideFilterOptions, setHideFilterOptions] = useState(false);
  const [data, setData] = useState([]);
  const [openModalView, setOpenModalView] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]); // All saved filters for active tab
  //   const [journalData, setJournalData] = useState([]);
  const [emails, setEmails] = useState([]); // ✅ multiple emails
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [journalData, setJournalData] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const occupantsID = searchParams.get("projectId");
  const [chatUI, setChatUI] = useState(false);
  const [chatData, setChatData] = useState([]);
  const accessControlData = useSelector(
    (state) => state?.accessControlData?.data || [],
  );

  // State to track which dropdowns to show
  const [activeRelatedDropdowns, setActiveRelatedDropdowns] = useState([]);

  // This just controls the checkboxes in MoreFilterDropdown
  const [moreFilterSelectedIds, setMoreFilterSelectedIds] = useState([]);

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || [],
  );

  const roleOptionData = useSelector(
    (state) => state?.roleOptionData?.data || [],
  );

  const roleOptions = roleOptionData?.map((item) => ({
    id: item.id,
    label: item.role,
  }));

  const optionsMoreFilter = [
    { id: "1", label: "Created By", related: "TextFilterDropdown" },
    { id: "3", label: "Revision", related: "RevisionFilterDropdown" },
    { id: "4", label: "Alarm Date", related: "AlarmDateDropdown" },
  ];

  // const filterSessionKey = `${currentProject.id}-latestSearchFilter`;

  // Initialize selected filter ID
  const [selectedFilterId, setSelectedFilterId] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.selectedFilterId || null;
    }
    return null;
  });

  const defaultFilters = {
    occupants: { header: "equals", selectedIds: [] },
    createdBy: { header: "equals", selectedIds: [] },
    lastUpdateBy: { header: "equals", selectedIds: [] },
    requestType: { header: "equals", selectedIds: [] },
    role: { header: "equals", selectedIds: [] },

    subject: { header: "equals", value: "" },
    email: { header: "equals", value: "", emails: [] },
    pointId: { header: "equals", value: "" },

    createdOn: {
      header: "withinLast",
      value: {
        number: "",
        unit: "",
      },
    },
    lastUpdate: null,
    revision: { value: "latest" },
    status: { value: "" },
    textfields: "",
    rawJQL: [],
  };

  const [tabFilters, setTabFilters] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...{
          2: defaultFilters,
        },
        [parsed.activeTab]: parsed.filters || defaultFilters,
      };
    }
    return {
      2: defaultFilters,
    };
  });

  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.filters || defaultFilters;
    }
    return defaultFilters;
  });

  const [searchText, setSearchText] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.searchText || "";
    }
    return "";
  });

  // ✅ Count how many filters are currently applied
  const getAppliedFilterCount = (filters) => {
    let count = 0;

    // 1️⃣ Multi-select filters
    const multiSelectKeys = [
      "occupants",
      "createdBy",
      "lastUpdateBy",
      "requestType",
      "role",
    ];
    multiSelectKeys.forEach((key) => {
      if (filters[key]?.selectedIds?.length > 0) count++;
    });

    // 2️⃣ Text filters
    const textKeys = ["subject", "pointId"];
    textKeys.forEach((key) => {
      if (filters[key]?.value?.trim()) count++;
    });

    // 2️⃣ Email filters (arrays or non-empty strings)
    const emailKeys = ["email"];
    emailKeys.forEach((key) => {
      const val = filters[key]?.value;
      if (Array.isArray(val) ? val.length > 0 : val?.trim()) count++;
    });

    // 3️⃣ Textfields (global search)
    // if (filters.textfields?.trim()) count++;

    // 4️⃣ Date filters
    const dateKeys = ["createdOn", "lastUpdate", "alarmDate"];
    dateKeys.forEach((key) => {
      const val = filters[key];
      if (
        val &&
        ((val.value?.number && val.value?.unit) || // withinLast/moreThan
          (val.value?.[0] && val.value?.[1])) // inRange or between
      ) {
        count++;
      }
    });

    // 5️⃣ Revision filter
    if (filters.revision?.value) count++;

    // 6️⃣ Status filter
    if (filters.status?.value) count++;

    // 7️⃣ Raw JQL (custom conditions)
    if (filters.rawJQL?.length > 0) count++;

    return count;
  };

  const appliedFilterCount = useMemo(
    () => getAppliedFilterCount(filters),
    [filters],
  );

  useEffect(() => {
    if (appliedFilterCount > 0) {
      setHideFilterOptions(true);
    }
  }, [appliedFilterCount]);

  // Save filters in sessionStorage whenever they change
  useEffect(() => {
    const filterToStore = {
      filters,
      activeTab,
      searchText,
      selectedFilterId, // ← include this!
    };
    sessionStorage.setItem(filterSessionKey, JSON.stringify(filterToStore));
  }, [filters, activeTab, searchText, selectedFilterId]);

  useEffect(() => {
    const savedFilter = sessionStorage.getItem(filterSessionKey);
    if (savedFilter) {
      const parsed = JSON.parse(savedFilter);

      // Set state
      setFilters(parsed.filters || defaultFilters);
      setEmails(parsed.filters?.email?.value || []);
      //   setActiveTab(parsed.activeTab || "1");
      setSearchText(parsed.searchText || "");

      // Update tabFilters
      setTabFilters((prev) => ({
        ...prev,
        [parsed.activeTab || "1"]: parsed.filters || defaultFilters,
      }));
    }
  }, []);

  //   useEffect(() => {
  //     console.log("activeTab", activeTab);
  //   }, [activeTab]);

  useEffect(() => {
    // Combine all text filter values for highlighting
    const textFilters = [
      filters.subject?.value,
      filters.pointId?.value,
      filters.email?.value,
      filters.textfields,
    ].filter(Boolean); // remove empty strings or nulls

    const combinedSearchKey = textFilters.join(" ");
    setSearchKey(combinedSearchKey);
  }, [filters, searchText]);

  const fetchSavedFilters = async (tabId, type, occupantsID) => {
    if (!currentProject?.id) return;

    try {
      const payload = {
        projectId: currentProject.id,
        type: tabId, // 1=file, 2=journal, 3=occupant
      };
      const response = await apiPost("/v1/getfilters", payload, false);
      if (response?.data) {
        setSavedFilters(response.data);

        const sessionFilter = sessionStorage.getItem(filterSessionKey);
        let applyBackendDefault = false;

        if (!sessionFilter) {
          // Session empty → apply backend default
          applyBackendDefault = true;
        } else {
          const parsed = JSON.parse(sessionFilter);
          const sessionFilters = parsed.filters || {};
          const isSameAsDefault =
            JSON.stringify(sessionFilters) === JSON.stringify(defaultFilters);

          if (isSameAsDefault) {
            // Session has only default filters → backend default should apply
            applyBackendDefault = true;
          }
        }

        if (applyBackendDefault) {
          // 🟢 Backend default found → apply AS-IS (no occupant change)
          const defaultFilter = response.data.find((f) => f.isDefault);

          if (defaultFilter) {
            applyFilter(defaultFilter);
          } else {
            // ⚠️ No backend default → apply frontend default and inject occupantsID if type=1
            const modifiedDefault = {
              filters: {
                ...defaultFilters,
                ...(type == 1 && occupantsID
                  ? {
                      occupants: {
                        ...defaultFilters.occupants,
                        selectedIds: [occupantsID],
                      },
                    }
                  : {}),
              },
            };
            applyFilter(modifiedDefault);
          }
        } else {
          // 🟡 Session exists → update occupants if type=1
          const parsed = JSON.parse(sessionFilter);
          let updatedFilters = { ...parsed.filters };

          if (type == 1 && occupantsID) {
            updatedFilters = {
              ...updatedFilters,
              occupants: {
                ...updatedFilters.occupants,
                selectedIds: [occupantsID], // always replace with current occupant
              },
            };
          }

          const newSession = { ...parsed, filters: updatedFilters };
          setFilters(updatedFilters);
          sessionStorage.setItem(filterSessionKey, JSON.stringify(newSession));
        }
      }
    } catch (error) {
      console.error("Error fetching saved filters:", error);
      setSavedFilters([]);
    }
  };

  // Update applyFilter
  useEffect(() => {
    fetchSavedFilters(activeTab, type, occupantsID);
  }, [currentProject?.id, activeTab]);

  useEffect(() => {
    if (type == 1 && occupantsID) {
      fetchSavedFilters(activeTab, type, occupantsID);
    }
  }, [occupantsID]);

  const applyFilter = (filter) => {
    if (!filter) {
      sessionStorage.removeItem(filterSessionKey);
      setFilters(defaultFilters);
      setSelectedFilterId(null);
      setTabFilters((prev) => ({
        ...prev,
        [activeTab]: defaultFilters,
      }));
      setSearchText("");
      setCurrentPage(1);
      return;
    }

    const newFilters = filter.filters || {};
    setFilters(newFilters);
    setSelectedFilterId(filter.id); // update selected filter
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: newFilters,
    }));
    setSearchText(newFilters.textfields || "");
    setCurrentPage(1);

    // Save to session
    const filterToStore = {
      filters: newFilters,
      activeTab,
      searchText: newFilters.textfields || "",
      selectedFilterId: filter.id, // store applied filter ID
    };
    sessionStorage.setItem(filterSessionKey, JSON.stringify(filterToStore));
  };

  // Callback from MoreFilterDropdown
  const handleOpenRelatedDropdown = (relatedArray) => {
    setActiveRelatedDropdowns(relatedArray || []);

    const unselected = [
      "TextFilterDropdown",
      "RevisionFilterDropdown",
      "AlarmDateDropdown",
    ].filter((d) => !relatedArray.includes(d));

    unselected.forEach((dropdown) => {
      if (dropdown === "TextFilterDropdown") {
        updateFilter("createdBy", "");
        setMoreFilterSelectedIds((prev) => prev.filter((id) => id !== "1"));
      }

      if (dropdown === "RevisionFilterDropdown") {
        updateFilter("revision", { value: "" });
        setMoreFilterSelectedIds((prev) => prev.filter((id) => id !== "3"));
      }
      if (dropdown === "AlarmDateDropdown") {
        updateFilter("alarmDate", "");
        setMoreFilterSelectedIds((prev) => prev.filter((id) => id !== "4"));
      }
    });
  };

  const handleClear = (isClearAll = false) => {
    // Reset filters to default
    // setFilters({});
    if (isClearAll) {
      setFilters({});
    } else {
      setFilters(defaultFilters);
    }
    setEmails([]);
    setChatData([]);
    // Clear checkboxes and dropdown states
    setMoreFilterSelectedIds([]);
    setActiveRelatedDropdowns([]);
    // Clear search text
    setSearchText("");
    // Clear selected filter
    setSelectedFilterId(null);
    // Reset tabFilters for current tab
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: defaultFilters,
    }));
    // Remove session storage entry
    sessionStorage.removeItem(filterSessionKey);
    // Reset pagination
    setCurrentPage(1);
  };

  // Debounce effect
  useEffect(() => {
    // 🧠 If searchText is empty → reset filters immediately
    if (searchText.trim() === "") {
      setFilters((prev) => ({
        ...prev,
        textfields: "",
      }));
      setCurrentPage(1);
      return; // skip debounce logic
    }

    // 🕒 Otherwise run debounce when user types
    const handler = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        textfields: searchText,
      }));
      setCurrentPage(1);
    }, 500);

    // 🧹 Cleanup debounce
    return () => clearTimeout(handler);
  }, [searchText]);

  const openSaveFilterModal = () => setOpenModalView(true);

  const RevisionRadioOptions = [
    { key: "latest", label: "Latest Threads" },
    { key: "all", label: "All Threads" },
  ];
  const statuisRadioOptions = [
    { key: "1", label: "Active" },
    { key: "0", label: "Close" },
  ];

  const getAddressBookData = async () => {
    try {
      const url = `/v1/addressbook/${currentProject?.id}/${projectType.property}`;
      const response = await apiGet(url);
      const mergedData = response.data;
      setData(mergedData);
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    getAddressBookData();
  }, [currentProject?.id]);

  // Convert occupant data to {id, label} format for MainDropdown
  const options = journalData?.occupantList
    ?.map((item) => ({
      id: item.id,
      label: `${item.occupantName} (${item.occupantNo})`,
    }))
    ?.sort((a, b) => a.label.localeCompare(b.label));

  const userOptions = data.map((item) => ({
    id: item.id,
    label: `${item.fullName}`,
  }));

  const handleAdvancedOptions = () => setHideFilterOptions(!hideFilterOptions);

  const handleIntractiveSearch = () => {
    setChatUI(!chatUI);
    setHideFilterOptions(false);
    handleClear(true);
  };
  const handleIntractiveSearchClose = () => {
    setChatUI(!chatUI);
    setHideFilterOptions(false);
    handleClear(false);
  };

  const updateFilter = (filterKey, updates) => {
    setFilters((prev) => {
      const updatedFilters = updates
        ? { ...prev[filterKey], ...updates }
        : null;

      setTabFilters((prevTabs) => ({
        ...prevTabs,
        [activeTab]: {
          ...prevTabs[activeTab],
          [filterKey]: updatedFilters,
        },
      }));

      return { ...prev, [filterKey]: updatedFilters };
    });
  };

  // Helper to map dropdown ID to relatedDropdown key
  const getDropdownKeyById = (id) => {
    const map = {
      1: "TextFilterDropdown",
      3: "RevisionFilterDropdown",
      4: "AlarmDateDropdown",
    };
    return map[id];
  };

  useEffect(() => {
    // Restore saved filters for the selected tab
    const saved = tabFilters[activeTab] || filters;
    setFilters(saved);
    // Fetch saved filters from backend
    fetchSavedFilters(activeTab, type, occupantsID);
    // Reset page
    setCurrentPage(1);
  }, [activeTab]);

  const getFilterData = async () => {
    setIsLoading(true);
    try {
      const searchQuery = buildSearchQuery() || "";
      if (!currentProject?.id) {
        setJournalData([]);
        setTotalRecord(0);
        return;
      }
      const payload = {
        projectId: currentProject?.id,
        searchQuery,
        pageSize,
        pageNo: currentPage,
        journalIds: chatData?.journal,
      };
      const response = await apiPost(
        `/v1/journal/advancedsearch`,
        payload,
        false,
      );
      setJournalData(response || []);
      setTotalRecord(response?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching document data:", error);
      setJournalData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFilterData();
  }, [filters, pageSize, chatData, currentPage]);

  const buildSearchQuery = () => {
    const conditions = [];

    // Multi-select filters
    const multiSelectFilters = [
      {
        field: "projectId",
        header: filters?.occupants?.header,
        selectedIds: filters?.occupants?.selectedIds,
      },
      {
        field: "createdBy",
        header: filters?.createdBy?.header,
        selectedIds: filters?.createdBy?.selectedIds,
      },
      {
        field: "lastUpdateBy",
        header: filters?.lastUpdateBy?.header,
        selectedIds: filters?.lastUpdateBy?.selectedIds,
      },
      {
        field: "requestType",
        header: filters?.requestType?.header,
        selectedIds: filters?.requestType?.selectedIds,
      },
      {
        field: "role",
        header: filters?.role?.header,
        selectedIds: filters?.role?.selectedIds,
      },
      {
        field: "email", // ✅ handle email here as multi-select
        header: filters?.email?.header,
        selectedIds: filters?.email?.value,
      },
      // {
      //   field: "id",
      //   header: "equals",
      //   selectedIds: chatData?.journal,
      // },
    ];

    multiSelectFilters.forEach(({ field, header, selectedIds }) => {
      if (!selectedIds?.length) return;
      const operator = header === "equals" ? "IN" : "NOT IN";

      // Detect if IDs are numeric
      const isNumeric = selectedIds.every((id) => !isNaN(Number(id)));

      const formattedIds = selectedIds
        .map((id) => (isNumeric ? id : `"${id}"`))
        .join(",");

      conditions.push(`${field} ${operator} (${formattedIds})`);
    });

    // Text filters
    const textFilters = [
      //   { field: "note", ...filters.submissionNote },
      { field: "subject", ...filters.subject },
      { field: "pointId", ...filters.pointId },
    ];

    const addTextCondition = (field, value, header) => {
      if (!value) return;
      switch (header) {
        case "equals":
          conditions.push(`${field} = "${value}"`);
          break;
        case "not-equals":
          conditions.push(`${field} != "${value}"`);
          break;
        case "contains":
          conditions.push(`${field} ~ "${value}"`);
          break;
        case "startsWith":
          conditions.push(`${field} STARTS WITH "${value}"`);
          break;
        case "endsWith":
          conditions.push(`${field} ENDS WITH "${value}"`);
          break;
        default:
          break;
      }
    };

    textFilters.forEach(({ field, value, header }) => {
      if (field === "pointId") {
        // Handle EMPTY case
        if (value === "__IS_EMPTY__") {
          conditions.push(`pointId IS EMPTY`);
          return;
        }

        // Handle NOT EMPTY case
        if (value === "__IS_NOT_EMPTY__") {
          conditions.push(`pointId IS NOT EMPTY`);
          return;
        }
      }

      // Normal text handling
      addTextCondition(field, value, header);
    });

    // TextFields quick search
    if (filters.textfields && filters.textfields.trim() !== "") {
      conditions.push(`textfields ~ "${filters.textfields.trim()}"`);
    }

    // --- Date filters: CreatedOn & LastUpdate ---
    ["createdOn", "lastUpdate"].forEach((field) => {
      const val = filters[field];
      if (!val) return; // skip if null

      let conditionStr = "";

      switch (val.header) {
        case "withinLast":
          if (val.value?.number) {
            conditionStr = `${field} last ${val.value.number} ${val.value.unit}`;
          }
          break;

        case "moreThan":
          if (val.value?.number) {
            conditionStr = `${field} moreThan ${val.value.number} ${val.value.unit}`;
          }
          break;

        case "inRange":
          if (val.value?.[0] && val.value?.[1]) {
            conditionStr = `${field} inRange "${val.value[0]}" "${val.value[1]}"`;
          }
          break;

        default:
          break;
      }

      if (conditionStr) conditions.push(conditionStr);
    });

    // --- Date filters: CreatedOn & LastUpdate ---
    ["alarmDate"].forEach((field) => {
      const val = filters[field];
      if (!val) return; // skip if null

      let conditionStr = "";

      switch (val.header) {
        case "BETWEEN":
          if (val.value?.[0] && val.value?.[1]) {
            conditionStr = `${field} BETWEEN "${val.value[0]}" "${val.value[1]}"`;

            // Include sent if available
            if (val.sent !== undefined) {
              conditionStr += ` AND sent = ${val.sent}`;
            }
          }
        default:
          break;
      }

      if (conditionStr) conditions.push(conditionStr);
    });

    // --- Revision filter ---
    if (filters.revision) {
      const revHeader = filters.revision.value;
      switch (revHeader) {
        case "latest":
          conditions.push("revision = latest");
          break;
        case "all":
          conditions.push("revision = all");
          break;
        default:
          break;
      }
    }

    // --- Revision filter ---
    if (filters.status) {
      const revHeader = filters.status.value;
      switch (revHeader) {
        case "1":
          conditions.push("status = 1");
          break;
        case "0":
          conditions.push("status = 0");
          break;
        default:
          break;
      }
    }

    // // --- Status filter ---
    // if (filters.status) {
    //   conditions.push(`status = ${filters.status.value}`);
    // }

    // Additional advanced filters: allow raw JQL conditions from frontend
    if (filters.rawJQL && filters.rawJQL.length) {
      conditions.push(...filters.rawJQL);
    }

    return conditions.join(" AND ");
  };

  const deriveSelectedIdsFromFilters = () => {
    const ids = [];
    if (filters.createdBy?.selectedIds?.length > 0) ids.push("1"); // TextFilterDropdown
    if (filters.status?.value) ids.push("2"); // RevisionFilterDropdown
    if (filters.revision?.value) ids.push("3"); // RevisionFilterDropdown
    if (filters.alarmDate) ids.push("4"); // AlarmDateDropdown
    return ids;
  };

  // Sync checkboxes when filters are first loaded or a filter is applied
  useEffect(() => {
    const selectedIdsFromFilters = deriveSelectedIdsFromFilters();
    setMoreFilterSelectedIds(selectedIdsFromFilters);

    const relatedDropdowns = selectedIdsFromFilters.map(getDropdownKeyById);
    setActiveRelatedDropdowns(relatedDropdowns);
  }, [selectedFilterId]); // only when a filter is applied/loaded

  const handleMoreFilterChange = (ids) => {
    const removedIds = moreFilterSelectedIds.filter((id) => !ids.includes(id));

    removedIds.forEach((id) => {
      const dropdownKey = getDropdownKeyById(id);
      if (dropdownKey === "TextFilterDropdown")
        updateFilter("createdBy", { selectedIds: [] });
      else if (dropdownKey === "RevisionFilterDropdown")
        updateFilter("revision", { value: "" });
      else if (dropdownKey === "AlarmDateDropdown")
        updateFilter("alarmDate", { selectedIds: [] });
    });

    setMoreFilterSelectedIds(ids);
    setActiveRelatedDropdowns(ids.map(getDropdownKeyById));
  };

  return (
    <>
      <SC.Container>
        {/* Search Row */}
        <SC.SearchRow>
          <SC.LeftControls>
            {!chatUI && (
              <>
                <SC.InputContiner>
                  <SC.SearchIcon />
                  <SC.InputStyle
                    placeholder="Search"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value.trimStart())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && searchText.trim() !== "") {
                        getFilterData();
                      }
                    }}
                  />
                </SC.InputContiner>

                <SC.AdvancedOptionsButton onClick={handleAdvancedOptions}>
                  Advanced Options
                  {appliedFilterCount > 0 && (
                    <SC.Badge>{appliedFilterCount}</SC.Badge>
                  )}
                  {hideFilterOptions ? (
                    <InnoIcons.KeyboardArrowUpIcon />
                  ) : (
                    <InnoIcons.KeyboardArrowDownIcon />
                  )}
                </SC.AdvancedOptionsButton>

                {AIServices() &&
                  accessControlData.canAccessInteractiveSearch == 1 && (
                    <Button
                      caption={"Interactive Search"}
                      onClick={handleIntractiveSearch}
                      color="black"
                      fontSize="12px"
                      leftIcon={
                        <InnoIcons.AutoAwesomeIcon
                          sx={{
                            fontSize: "20px",
                            color: theme.colors.themeColor,
                          }}
                        />
                      }
                      style={{
                        background: "white",
                        color: theme.colors.white,
                        // minWidth: "150px",
                        height: "40px",
                        borderRadius: "6px",
                        border: `1px solid ${theme.colors.themeColor}`,
                      }}
                    />
                  )}
              </>
            )}
          </SC.LeftControls>

          <SC.SavedFilterContainer>
            {hideFilterOptions && savedFilters.length > 0 && (
              <SavedFilterDropdown
                label=""
                options={savedFilters}
                selectedId={selectedFilterId}
                onSelectionChange={(filter) => {
                  applyFilter(filter);
                  setSelectedFilterId(filter.id);
                }}
                handleClear={() => {
                  applyFilter(null);
                  setSelectedFilterId(null);
                  handleClear();
                }}
              />
            )}
          </SC.SavedFilterContainer>
        </SC.SearchRow>

        {/* Advanced Filter Section */}
        {hideFilterOptions && (
          <SC.FilterSection>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                // marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <SC.FilterRow>
                  <SC.FilterTitle>Advanced Filter</SC.FilterTitle>
                  <MultiSelectDropdown
                    label="Occupant"
                    headerOptionsValueName={"Occupant"} // e.g., equals / not-equals
                    selectedHeaderId={filters?.occupants?.header}
                    onHeaderChange={(header) =>
                      updateFilter("occupants", { header })
                    }
                    options={options} // array of { id, label }
                    selectedIds={filters?.occupants?.selectedIds}
                    onSelectionChange={(selectedIds) =>
                      updateFilter("occupants", { selectedIds })
                    }
                  />

                  <TextFilterDropdown
                    label="Journal Subject"
                    headerOptionsValuename={"Journal Subject"}
                    selectedHeaderId={filters?.subject?.header}
                    value={filters?.subject?.value}
                    onHeaderChange={(header) =>
                      updateFilter("subject", { header })
                    }
                    onUpdate={({ headerId, value }) =>
                      updateFilter("subject", { header: headerId, value })
                    }
                  />

                  <MultiSelectDropdown
                    label="Request Type"
                    selectedHeaderId={filters?.requestType?.header}
                    headerOptionsValueName={"Request Type"}
                    onHeaderChange={(header) =>
                      updateFilter("requestType", { header })
                    }
                    options={requestTypeForUpdate}
                    selectedIds={filters?.requestType?.selectedIds}
                    onSelectionChange={(selectedIds) =>
                      updateFilter("requestType", { selectedIds })
                    }
                  />
                  <MultiSelectDropdown
                    label="Request To"
                    selectedHeaderId={filters?.role?.header}
                    headerOptionsValueName={"Request To"}
                    onHeaderChange={(header) =>
                      updateFilter("role", { header })
                    }
                    options={roleOptions}
                    selectedIds={filters?.role?.selectedIds}
                    onSelectionChange={(selectedIds) =>
                      updateFilter("role", { selectedIds })
                    }
                  />

                  <CreatedOnDropdown
                    label="Created"
                    field="createdOn"
                    value={filters.createdOn}
                    onUpdate={(value) => updateFilter("createdOn", value)}
                  />

                  <EmailFilterDropdown
                    label="Sent Email"
                    headerOptionsValuename={"Sent Email"}
                    selectedHeaderId={filters?.email?.header}
                    value={filters?.email?.value}
                    onHeaderChange={(header) =>
                      updateFilter("email", { header })
                    }
                    onUpdate={({ headerId, value }) =>
                      updateFilter("email", { header: headerId, value })
                    }
                    emails={emails}
                    setEmails={setEmails}
                  />

                  <RevisionFilterDropdown
                    label="Status"
                    value={filters?.status?.value}
                    onUpdate={(value) =>
                      updateFilter("status", { value: value || "" }, "2")
                    }
                    radioOptions={statuisRadioOptions}
                  />

                  <TextFilterDropdown
                    label="Ingested"
                    headerOptionsValuename={"Ingested"}
                    selectedHeaderId={filters?.pointId?.header}
                    value={filters?.pointId?.value}
                    onHeaderChange={(header) =>
                      updateFilter("pointId", { header })
                    }
                    onUpdate={({ headerId, value }) =>
                      updateFilter("pointId", { header: headerId, value }, "1")
                    }
                    isEmptyOption={true}
                    pointIdOption={true}
                  />

                  <MoreFilterDropdown
                    label="More Filter"
                    selectedIds={moreFilterSelectedIds}
                    onSelectionChange={handleMoreFilterChange}
                    onOpenRelatedDropdown={handleOpenRelatedDropdown}
                    options={optionsMoreFilter}
                  />

                  {(activeRelatedDropdowns || []).includes(
                    "TextFilterDropdown",
                  ) && (
                    <MultiSelectDropdown
                      label="Created By"
                      headerOptionsValueName={"Created By"}
                      selectedHeaderId={filters?.createdBy?.header}
                      onHeaderChange={(header) =>
                        updateFilter("createdBy", { header })
                      }
                      options={userOptions}
                      selectedIds={filters?.createdBy?.selectedIds}
                      onSelectionChange={(selectedIds) =>
                        updateFilter("createdBy", { selectedIds })
                      }
                    />
                  )}

                  {(activeRelatedDropdowns || []).includes(
                    "RevisionFilterDropdown",
                  ) && (
                    <RevisionFilterDropdown
                      label="Threads"
                      value={filters?.revision?.value}
                      onUpdate={(value) =>
                        updateFilter("revision", { value: value || "" }, "3")
                      }
                      radioOptions={RevisionRadioOptions}
                    />
                  )}

                  {(activeRelatedDropdowns || []).includes(
                    "AlarmDateDropdown",
                  ) && (
                    <AlarmDateDropdown
                      label="Alarm Date"
                      field="alarmDate"
                      value={filters.alarmDate}
                      onUpdate={(value) =>
                        updateFilter("alarmDate", value, "4")
                      }
                    />
                  )}

                  <SC.FilterActions>
                    <Button
                      caption="Clear Filter"
                      onClick={handleClear}
                      fontSize="12px"
                      style={{
                        background: theme.colors.cancelButton,
                        color: theme.colors.white,
                        minWidth: "90px",
                        borderRadius: "5px",
                        height: "35px",
                        fontSize: "12px",
                      }}
                    />
                    <Button
                      caption={"Save Filter"}
                      onClick={openSaveFilterModal}
                      color="white"
                      fontSize="12px"
                      style={{
                        background: theme.gradient.themeGradient,
                        color: theme.colors.white,
                        minWidth: "90px",
                        height: "35px",
                        borderRadius: "5px",
                      }}
                    />
                  </SC.FilterActions>
                </SC.FilterRow>
              </div>
            </div>
          </SC.FilterSection>
        )}
      </SC.Container>

      {/* {journalData && (
        <SC.CardContainer>
          <p style={{ fontWeight: 500 }}>
            Total Record: {journalData?.totalCount || 0}
          </p>
          <ActiveJournals
            data={journalData}
            pageSize={pageSize}
            currentPage={currentPage}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            isLoading={isLoading}
          />
        </SC.CardContainer>
      )} */}

      <div
        style={{
          display: "flex",
          flexDirection: chatUI && window.innerWidth <= 768 ? "column" : "row",
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        <div
          style={{
            flex: chatUI && window.innerWidth > 768 ? "0 0 67%" : "1 1 auto",
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            minWidth: 0,
          }}
        >
          <SC.CardContainer>
            <p style={{ fontWeight: 500 }}>
              Total Record:{" "}
              {journalData?.totalCount || journalData?.data?.length || 0}
            </p>
            <ActiveJournals
              data={journalData}
              pageSize={pageSize}
              currentPage={currentPage}
              setPageSize={setPageSize}
              setCurrentPage={setCurrentPage}
              isLoading={isLoading}
              chatUI={chatUI}
            />
          </SC.CardContainer>
        </div>

        {chatUI && (
          <div
            style={{
              flex: window.innerWidth > 768 ? "0 0 33%" : "1 1 auto",
              width: window.innerWidth > 768 ? "33%" : "100%",
              height: window.innerWidth > 768 ? "100%" : "100vh",
              position: window.innerWidth > 768 ? "sticky" : "fixed",
              bottom: window.innerWidth > 768 ? "auto" : 0,
              right: 0,
              top: window.innerWidth > 768 ? 130 : 0,
              zIndex: 20,
              background: "#f6f6f6",
              borderLeft: window.innerWidth > 768 ? "1px solid #eee" : "none",
              boxShadow:
                window.innerWidth <= 768
                  ? "0 -4px 16px rgba(0,0,0,0.15)"
                  : "none",
            }}
          >
            <InteractiveSearchUI
              handleIntractiveSearch={handleIntractiveSearchClose}
              setChatData={setChatData}
              jobType={jobTypes.ADDJOURNAL}
              queryType={queryTypes.journal}
            />
          </div>
        )}
      </div>

      {openModalView && (
        <SaveFilterModal
          openModalView={openModalView}
          onClose={() => setOpenModalView(false)}
          filters={filters}
          searchQuery={buildSearchQuery()}
          type={activeTab}
          projectId={currentProject?.id}
          fetchSavedFilters={fetchSavedFilters}
        />
      )}
    </>
  );
};

export default JournalSearchInterface;
